import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CircleBranding } from 'src/api/circles';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { selectTribeDetails } from 'src/redux/slices/loggedUserSlice';
import { ResolvedBranding, hasBranding, resolveBranding } from './branding';

/** Circle id for routes that belong to a circle (`/circle/:id/...`). */
export const circleIdFromPathname = (pathname: string): string | undefined =>
  pathname.match(/^\/circle\/([^/]+)/)?.[1];

/** Circle id of the public OTH registration page (`/oth/registration/:id`). */
export const registrationCircleIdFromPathname = (
  pathname: string
): string | undefined => pathname.match(/^\/oth\/registration\/([^/]+)/)?.[1];

/**
 * Resolves the active circle's branding. The circle comes from the URL
 * (or an explicit id); branding is read from the loaded circle details when
 * they belong to that circle, otherwise from the user's membership list that
 * `GET /users/me` returns. Returns Dunbar defaults (isBranded=false) when
 * nothing is set, so callers can use it unconditionally.
 */
export const useBranding = (explicitCircleId?: string): ResolvedBranding => {
  const location = useLocation();
  const circleId =
    explicitCircleId ??
    circleIdFromPathname(location.pathname) ??
    registrationCircleIdFromPathname(location.pathname);

  const circleData = useAppSelector((state) => state.circleDetail.circleData);
  const tribeDetails = useAppSelector(selectTribeDetails);
  // Set by public circle pages (registration) for visitors who are not members
  const publicBranding = useAppSelector((state) => state.publicBranding);

  const raw: CircleBranding | undefined = useMemo(() => {
    if (!circleId) {
      return undefined;
    }
    if (circleData?._id === circleId && hasBranding(circleData.branding)) {
      return circleData.branding;
    }
    const membership = tribeDetails?.find((tribe) => tribe._id === circleId);
    if (hasBranding(membership?.branding)) {
      return membership?.branding;
    }
    return publicBranding.circleId === circleId &&
      hasBranding(publicBranding.branding)
      ? publicBranding.branding
      : undefined;
  }, [circleId, circleData, tribeDetails, publicBranding]);

  return useMemo(() => resolveBranding(raw), [raw]);
};
